import { SearchableSelect } from 'components';
import { ChangeEventHandler, FC, RefObject, useCallback, useMemo, useRef } from 'react';
import { validateEmail, validateUsername } from 'services';
import { Location, Role, UserProfile } from 'types';

import './new-user.scss';

type UsernameFieldProps = {
  user: Partial<UserProfile>;
  setUsername: (username?: string) => any;
  setUser: (user: Partial<UserProfile>) => void;
  required: boolean;
  error?: (username: string) => string;
  usernameRef: RefObject<HTMLInputElement>;
};

const UsernameField: FC<UsernameFieldProps> = ({
  user,
  setUsername,
  setUser,
  required = false,
  error,
  usernameRef
}) => {
  const loadingRef = useRef<HTMLSpanElement>(null);
  const successRef = useRef<HTMLSpanElement>(null);
  const warningRef = useRef<HTMLSpanElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const setAdornment = (visible?: 'loading' | 'success' | 'warning') => {
    loadingRef.current?.toggleAttribute('hidden', visible !== 'loading');
    successRef.current?.toggleAttribute('hidden', visible !== 'success');
    warningRef.current?.toggleAttribute('hidden', visible !== 'warning');
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    async e => {
      setAdornment(undefined);
      e.target.setCustomValidity('');
      if (!e.target.value) {
        if (user.username) {
          setUsername(undefined);
        }
      } else if (e.target.value !== user.username) {
        if(!e.target.value.match(/^(?!.*@)[\p{L}\p{M}\p{N}\p{P}\p{S}]{1,128}$/u)) {
          e.target.setCustomValidity('Username is invalid');
          e.target.reportValidity();
          setAdornment('warning');
        } else if (error?.(e.target.value)) {
          e.target.setCustomValidity(error?.(e.target.value));
          e.target.reportValidity();
          setAdornment('warning');
        } else {
          setAdornment('loading');
          const response = await validateUsername(e.target.value);
          const isValid = await response.json();
          if (isValid) {
            setAdornment('success');
            setUser({ ...user, username: e.target.value });
            e.target.setCustomValidity('');
          } else {
            setAdornment('warning');
            setUser({ ...user, username: '' });
            e.target.setCustomValidity('Username already exists');
          }
        }
      }
    },
    [user, setUser, setUsername, error]
  );

  const debounceValidation: ChangeEventHandler<HTMLInputElement> = useMemo(() => {
    return e => {
      clearTimeout(timeoutRef.current);
      e.target.value = e.target.value.trim();
      timeoutRef.current = setTimeout(() => handleChange(e), 1000);
    };
  }, [handleChange]);

  return (
    <div className="username-input">
      <input
        type="text"
        className="mt-2 w-100"
        defaultValue={user.username ?? ''}
        onChange={debounceValidation}
        ref={usernameRef}
        required={required}
      />
      <button
        className="btn-back clear-btn"
        type="button"
        onClick={() => {
          if (usernameRef.current) {
            usernameRef.current.value = '';
            debounceValidation({ target: usernameRef.current } as any);
          }
        }}
        hidden
      >
        <span className="material-icons icon-large">close</span>
      </button>
      <span className="adornment loading" ref={loadingRef} hidden>
        <svg viewBox="0 0 24 24">
          <circle r={9} cx={12} cy={12} />
        </svg>
      </span>
      <span className="adornment material-icons icon-large" ref={successRef} hidden>
        done
      </span>
      <span
        className="adornment material-icons icon-large warning"
        onMouseEnter={e => {
          if (usernameRef.current && !e.currentTarget.hidden) {
            usernameRef.current.reportValidity();
          }
        }}
        onClick={e => {
          if (usernameRef.current && !e.currentTarget.hidden) {
            usernameRef.current.reportValidity();
            debounceValidation({ target: usernameRef.current } as any);
          }
        }}
        ref={warningRef}
        hidden
      >
        warning
      </span>
    </div>
  );
};

type EmailFieldProps = {
  user: Partial<UserProfile>;
  setEmail: (email?: string) => any;
  setUser: (user: Partial<UserProfile>) => void;
  required: boolean;
  error?: (email: string) => string;
  emailRef: RefObject<HTMLInputElement>;
};

const EmailField: FC<EmailFieldProps> = ({ user, setEmail, setUser, required = false, error, emailRef }) => {
  const loadingRef = useRef<HTMLSpanElement>(null);
  const successRef = useRef<HTMLSpanElement>(null);
  const warningRef = useRef<HTMLSpanElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const setAdornment = (visible?: 'loading' | 'success' | 'warning') => {
    loadingRef.current?.toggleAttribute('hidden', visible !== 'loading');
    successRef.current?.toggleAttribute('hidden', visible !== 'success');
    warningRef.current?.toggleAttribute('hidden', visible !== 'warning');
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    async e => {
      setAdornment(undefined);
      e.target.setCustomValidity('');
      if (!e.target.value) {
        if (user.username) {
          setEmail(undefined);
        }
      } else if (e.target.value !== user.email) {
        if (!e.target.value.match(/^[a-zA-Z0-9_!#$%&’*+/=?`{|}~^.-]+@[a-zA-Z0-9.-]+$/)) {
          e.target.setCustomValidity('Email is invalid');
          e.target.reportValidity();
          setAdornment('warning');
        } else if (error?.(e.target.value)) {
          e.target.setCustomValidity(error?.(e.target.value));
          e.target.reportValidity();
          setAdornment('warning');
        } else {
          setAdornment('loading');
          const response = await validateEmail(e.target.value);
          const isValid = await response.json();
          if (isValid) {
            setAdornment('success');
            setUser({ ...user, email: e.target.value });
            e.target.setCustomValidity('');
          } else {
            setAdornment('warning');
            setUser({ ...user, email: '' });
            e.target.setCustomValidity('Email already exists');
          }
        }
      }
    },
    [user, setUser, setEmail, error]
  );

  const debounceValidation: ChangeEventHandler<HTMLInputElement> = useMemo(() => {
    return e => {
      clearTimeout(timeoutRef.current);
      e.target.value = e.target.value.trim();
      timeoutRef.current = setTimeout(() => handleChange(e), 1000);
    };
  }, [handleChange]);

  return (
    <div className="email-input">
      <input
        type="text"
        className="mt-2 w-100"
        defaultValue={user.email ?? ''}
        onChange={debounceValidation}
        ref={emailRef}
        required={required}
      />
      <button
        className="btn-back clear-btn"
        type="button"
        onClick={() => {
          if (emailRef.current) {
            emailRef.current.value = '';
            debounceValidation({ target: emailRef.current } as any);
          }
        }}
        hidden
      >
        <span className="material-icons icon-large">close</span>
      </button>
      <span className="adornment loading" ref={loadingRef} hidden>
        <svg viewBox="0 0 24 24">
          <circle r={9} cx={12} cy={12} />
        </svg>
      </span>
      <span className="adornment material-icons icon-large" ref={successRef} hidden>
        done
      </span>
      <span
        className="adornment material-icons icon-large warning"
        onMouseEnter={e => {
          if (emailRef.current && !e.currentTarget.hidden) {
            emailRef.current.reportValidity();
          }
        }}
        onClick={e => {
          if (emailRef.current && !e.currentTarget.hidden) {
            emailRef.current.reportValidity();
            debounceValidation({ target: emailRef.current } as any);
          }
        }}
        ref={warningRef}
        hidden
      >
        warning
      </span>
    </div>
  );
};

type UserInformationProps = {
  user: Partial<UserProfile>;
  userLocations: Map<number, Location>;
  allLocations: Map<number, Location>;
  setUser: (user: Partial<UserProfile>) => void;
  usernameRef: RefObject<HTMLInputElement>;
  emailRef: RefObject<HTMLInputElement>;
  roleRef: RefObject<HTMLSelectElement>;
};

export const UserInformation: FC<UserInformationProps> = ({
  user,
  userLocations,
  allLocations,
  setUser,
  usernameRef,
  emailRef,
  roleRef
}) => {
  const setUsername = useCallback((username?: string) => setUser({ ...user, username }), [user, setUser]);
  const setEmail = useCallback((email?: string) => setUser({ ...user, email }), [user, setUser]);

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4>GENERAL INFORMATION</h4>
      </div>
      <div className="card-body">
        <div className="grid-4 p-2">
          <div className="position-relative">
            <h6 className="text-info-dark">USERNAME</h6>
            <UsernameField
              key="username"
              usernameRef={usernameRef}
              user={user}
              setUser={setUser}
              setUsername={setUsername}
              required
            />
          </div>
          <div className="position-relative">
            <h6 className="text-info-dark">EMAIL</h6>
            <EmailField key="email" emailRef={emailRef} user={user} setUser={setUser} setEmail={setEmail} required />
          </div>
          <div className="position-relative">
            <h6 className="text-info-dark">ROLE</h6>
            <select
              className="w-100 mt-2"
              value={user.role ?? Role.NONE}
              ref={roleRef}
              onChange={e => setUser({ ...user, role: e.target.value as Role })}
              required
            >
              <option value={Role.NONE} disabled hidden></option>
              <option value={Role.ADMIN}>ADMIN</option>
              <option value={Role.BILLING_ADMIN}>BILLING ADMIN</option>
              <option value={Role.SENIOR_BOOKING}>BOOKING - SENIOR</option>
              <option value={Role.BOOKING}>BOOKING</option>
              <option value={Role.CARRIER}>CARRIER</option>
              <option value={Role.ADMIN_CUSTOMER}>ADMIN CUSTOMER</option>
              <option value={Role.CUSTOMER}>CUSTOMER</option>
              <option value={Role.EXTERNAL}>EXTERNAL</option>
              <option value={Role.GATE}>GATE</option>
              <option value={Role.GATE_BOOKING}>GATE - BOOKING</option>
            </select>
          </div>
          <div className="position-relative">
            <h6 className="text-info-dark">DEFAULT LOCATION</h6>
            <div className="mt-2 w-100 default-location-input">
              <SearchableSelect
                required
                options={
                  userLocations?.size > 0
                    ? [...userLocations.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))
                    : [...allLocations.values()].map(({ id, name }) => ({ label: name, value: `${id}` }))
                }
                onValueSelected={id =>
                  userLocations.size > 0
                    ? setUser({ ...user, defaultLocation: userLocations.get(Number(id)) })
                    : setUser({ ...user, defaultLocation: allLocations.get(Number(id)) })
                }
                value={`${user.defaultLocation?.id ?? ''}`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
